/**
 * useDesignWebSocket Hook
 *
 * React hook for managing Design Mode WebSocket connections.
 * Provides real-time multi-window synchronization for Design (Design) mode.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  designWebsocketManager,
  DesignWebSocketMessage,
} from '../services/designWebsocketService';

export interface UseDesignWebSocketOptions {
  onMessage?: (message: DesignWebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onReconnect?: () => void;
}

export type DesignSocketState = 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'UNKNOWN';

export function useDesignWebSocket() {
  const [socketState, setSocketState] = useState<DesignSocketState>('CLOSED');
  const [lastSocketEvent, setLastSocketEvent] = useState<string>('');
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const wsProjectRef = useRef<string | null>(null);
  const wsConnectPromiseRef = useRef<Promise<void> | null>(null);
  const wsConnectingProjectRef = useRef<string | null>(null);

  const connectToProject = useCallback(
    async (projectId: string, options: UseDesignWebSocketOptions = {}): Promise<void> => {
      if (!projectId) return;

      console.log(`🔌 [Design] Connecting to WebSocket for project: ${projectId}`);

      // If already connected to this project, do nothing
      if (socketState === 'OPEN' && wsProjectRef.current === projectId) {
        console.log(`✅ [Design] Already connected to project ${projectId}`);
        return;
      }

      // If a connect is already in-flight for this project, reuse it
      if (wsConnectPromiseRef.current && wsConnectingProjectRef.current === projectId) {
        console.log(`🔄 [Design] Connection already in-flight for project ${projectId}`);
        return wsConnectPromiseRef.current;
      }

      const token = localStorage.getItem('token') || '';

      // Disconnect from previous project if needed
      if (wsProjectRef.current && wsProjectRef.current !== projectId) {
        try {
          designWebsocketManager.disconnect(wsProjectRef.current);
        } catch {
          // Ignore disconnect errors
        }
      }

      wsProjectRef.current = projectId;
      wsConnectingProjectRef.current = projectId;

      wsConnectPromiseRef.current = new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          wsConnectPromiseRef.current = null;
          wsConnectingProjectRef.current = null;
          reject(new Error('Design WebSocket connection timeout'));
        }, 5000);

        designWebsocketManager.connect(projectId, token, {
          onMessage: (message) => {
            console.log('📨 [Design] WebSocket message received:', message.type);
            setLastSocketEvent(message.type);
            options.onMessage?.(message);
          },
          onConnect: () => {
            clearTimeout(timeout);
            setSocketState('OPEN');
            setIsReconnecting(false);
            setReconnectAttempt(0);
            console.log('✅ [Design] WebSocket connected to project:', projectId);
            wsConnectPromiseRef.current = null;
            wsConnectingProjectRef.current = null;

            if (reconnectTimeoutRef.current) {
              window.clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }

            options.onConnect?.();
            resolve();
          },
          onReconnect: () => {
            clearTimeout(timeout);
            setSocketState('OPEN');
            setIsReconnecting(false);
            setReconnectAttempt(0);
            console.log('🔄 [Design] WebSocket reconnected:', projectId);
            wsConnectPromiseRef.current = null;
            wsConnectingProjectRef.current = null;

            if (reconnectTimeoutRef.current) {
              window.clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }

            options.onReconnect?.();
            resolve();
          },
          onDisconnect: () => {
            clearTimeout(timeout);
            setSocketState('CLOSED');
            setIsReconnecting(true);
            console.log('🔌 [Design] WebSocket disconnected:', projectId);
            wsConnectPromiseRef.current = null;
            wsConnectingProjectRef.current = null;

            if (reconnectTimeoutRef.current) {
              window.clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = window.setTimeout(() => {
              console.log('⏰ [Design] Reconnection timeout reached');
              setIsReconnecting(false);
              setReconnectAttempt(0);
            }, 15000);

            options.onDisconnect?.();
          },
          onError: () => {
            clearTimeout(timeout);
            setSocketState('UNKNOWN');
            setIsReconnecting(true);
            console.warn('❌ [Design] WebSocket error:', projectId);
            wsConnectPromiseRef.current = null;
            wsConnectingProjectRef.current = null;

            if (reconnectTimeoutRef.current) {
              window.clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = window.setTimeout(() => {
              console.log('⏰ [Design] Reconnection timeout after error');
              setIsReconnecting(false);
              setReconnectAttempt(0);
            }, 120000);

            options.onError;
            reject(new Error('Design WebSocket connection failed'));
          },
          onReconnectAttempt: (attempt) => {
            setReconnectAttempt(attempt);
            setIsReconnecting(true);
            console.log(`🔄 [Design] Reconnection attempt ${attempt} for project: ${projectId}`);
          },
        });
      });

      return wsConnectPromiseRef.current;
    },
    [socketState]
  );

  const disconnect = useCallback(() => {
    if (wsProjectRef.current) {
      designWebsocketManager.disconnect(wsProjectRef.current);
      wsProjectRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setSocketState('CLOSED');
    setIsReconnecting(false);
    setReconnectAttempt(0);
  }, []);

  const disconnectAll = useCallback(() => {
    designWebsocketManager.disconnectAll();
    wsProjectRef.current = null;

    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setSocketState('CLOSED');
    setIsReconnecting(false);
    setReconnectAttempt(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
      }
      // disconnectAll();
    };
  }, [disconnectAll]);

  return {
    socketState,
    lastSocketEvent,
    isReconnecting,
    reconnectAttempt,
    connectToProject,
    disconnect,
    disconnectAll,
    connectedProjectId: wsProjectRef.current,
  };
}
